export type DirtyFields = {
  [x: string]: boolean | DirtyFields | undefined;
  [x: number]: boolean | DirtyFields | undefined;
};

export function flattenDirtyFields(
  dirtyFields: DirtyFields,
  prefix?: string,
): string[] {
  let leaves: string[] = [];

  for (const key in dirtyFields) {
    if (Object.prototype.hasOwnProperty.call(dirtyFields, key)) {
      const value = dirtyFields[key];
      const shouldPreservePath = prefix?.startsWith('trips') || key === 'trips';
      const currentPath =
        shouldPreservePath && prefix ? `${prefix}.${key}` : key;
      if (typeof value === 'object' && value !== null) {
        const newPrefix = shouldPreservePath ? currentPath : undefined;
        leaves = leaves.concat(flattenDirtyFields(value, newPrefix));
      } else if (value === true) {
        leaves.push(currentPath);
      }
    }
  }
  return leaves;
}

/**This function is necessary because react-hook-form can mark fields as “dirty” even if they have not been modified by
 the user (for example, due to transformations during prefilling or calls to reset()).**/
export function filterDirtyFieldWithChanges(
  dirtyFields: DirtyFields,
  currentValues: Record<string, unknown>,
  originalValues: Record<string, unknown>,
): DirtyFields {
  const filtered: DirtyFields = {};

  Object.entries(dirtyFields).forEach(([key, isDirty]) => {
    if (!isDirty) {
      return;
    }

    if (isNestedDirtyFields(isDirty)) {
      const nestedFiltered = filterNestedDirtyFields(
        key,
        isDirty,
        currentValues,
        originalValues,
      );
      if (nestedFiltered) {
        filtered[key] = nestedFiltered;
      }
    } else if (isDirty) {
      if (hasValueChanged(currentValues[key], originalValues[key])) {
        filtered[key] = true;
      }
    }
  });

  return filtered;
}

function isNestedDirtyFields(value: unknown): value is DirtyFields {
  return typeof value === 'object' && value !== null;
}

function filterNestedDirtyFields(
  key: string,
  nestedDirtyFields: DirtyFields,
  currentValues: Record<string, unknown>,
  originalValues: Record<string, unknown>,
): DirtyFields | null {
  const currentValue = currentValues[key];
  const originalValue = originalValues[key];

  const nestedFiltered = filterDirtyFieldWithChanges(
    nestedDirtyFields,
    (currentValue as Record<string, unknown>) || {},
    (originalValue as Record<string, unknown>) || {},
  );

  return Object.keys(nestedFiltered).length > 0 ? nestedFiltered : null;
}

function hasValueChanged(
  currentValue: unknown,
  originalValue: unknown,
): boolean {
  return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
}
